import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {colors} from '../../utils/colors.js';
import Globe02Icon from '../Icons/Globe02Icon';
import CheckBroken from '../Icons/CheckBroken';
import IconButtonMenu from '../IconButtonMenu/IconButtonMenu';
import {TIMEZONE_OPTIONS, useTimezone} from '../../providers/TimezoneProvider.jsx';

const TIMEZONE_LABELS = {
  [TIMEZONE_OPTIONS.UTC]: 'UTC',
  [TIMEZONE_OPTIONS.HOTEL]: 'Hotel',
  [TIMEZONE_OPTIONS.BROWSER]: 'Browser',
};

const TIMEZONE_BADGES = {
  [TIMEZONE_OPTIONS.UTC]: 'UTC',
  [TIMEZONE_OPTIONS.HOTEL]: 'H',
  [TIMEZONE_OPTIONS.BROWSER]: 'B',
};

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const TimezoneMenuItem = ({option, isActive, onSelect}) => {
  const handleClick = () => {
    onSelect(option.value);
  };

  return (
    <IconButtonMenu.Item
      onClick={handleClick}
      subtitle={isActive ? 'Currently active' : undefined}
      endContent={isActive ? <CheckBroken color={colors.primaryColor} width={16} /> : undefined}
    >
      {option.label}
    </IconButtonMenu.Item>
  );
};

TimezoneMenuItem.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  isActive: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

const TimezoneMenu = ({posthog}) => {
  const {timezonePreference, setTimezonePreference, hotelTimezone} = useTimezone();

  const timezoneOptions = useMemo(() => [
    {value: TIMEZONE_OPTIONS.UTC, label: 'UTC'},
    {value: TIMEZONE_OPTIONS.HOTEL, label: `Hotel (${hotelTimezone})`},
    {value: TIMEZONE_OPTIONS.BROWSER, label: `Browser (${browserTimezone})`},
  ], [hotelTimezone]);

  const handleTimezoneChange = (timezone) => {
    setTimezonePreference(timezone);
    posthog?.capture('timezone:preference_changed', {
      timezone: timezone,
    });
  };

  return (
    <IconButtonMenu
      icon={<Globe02Icon color={colors.primaryColor} />}
      title="Select timezone"
      label={`Timezone: ${TIMEZONE_LABELS[timezonePreference]}`}
      badge={TIMEZONE_BADGES[timezonePreference]}
      anchorToHeaderOnMobile
    >
      {timezoneOptions.map(option => (
        <TimezoneMenuItem
          key={option.value}
          option={option}
          isActive={timezonePreference === option.value}
          onSelect={handleTimezoneChange}
        />
      ))}
    </IconButtonMenu>
  );
};

TimezoneMenu.propTypes = {
  posthog: PropTypes.object,
};

export default TimezoneMenu;
